import type { Deal, DealInput, CostEstimate, CostEstimateInput } from '@elka/types';

/**
 * New deal from an existing one ("duplicate deal"). Rebuilt explicitly (not spread) so we
 * never carry over the source deal's own identity/quoting/outcome state onto the copy.
 */
export function buildDuplicateDealInput(
  source: Deal,
  target: { name: string; companyId: string; companyName: string }
): DealInput {
  const sameCompany = target.companyId === source.companyId;

  const input: DealInput = {
    name: target.name,
    companyId: target.companyId,
    companyName: target.companyName,
    stage: 'proposal',
  };

  if (sameCompany && source.contactId !== undefined) input.contactId = source.contactId;
  if (sameCompany && source.contactName !== undefined) input.contactName = source.contactName;
  if (source.probability !== undefined) input.probability = source.probability;
  if (source.notes !== undefined) input.notes = source.notes;
  if (source.tenderSource !== undefined) input.tenderSource = source.tenderSource;
  if (source.tenderType !== undefined) input.tenderType = source.tenderType;
  if (source.pricingUiVariant !== undefined) input.pricingUiVariant = source.pricingUiVariant;

  return input;
}

/**
 * New cost estimate from an existing one, targeting the duplicated deal. Required numeric
 * fields are copied as-is — addCostEstimate recomputes the derived materials/labor/total/target
 * from items + selectedPrices on write, so these are just seed values.
 */
export function buildDuplicateEstimateInput(
  source: CostEstimate,
  target: { dealId: string; dealName: string; companyId: string; companyName: string }
): CostEstimateInput {
  const input: CostEstimateInput = {
    dealId: target.dealId,
    dealName: target.dealName,
    companyId: target.companyId,
    companyName: target.companyName,
    projectName: target.dealName,
    status: 'draft',
    items: source.items,
    laborDays: source.laborDays,
    dayRate: source.dayRate,
    laborCost: source.laborCost,
    materialsCost: source.materialsCost,
    totalCost: source.totalCost,
    marginPercent: source.marginPercent,
    targetPrice: source.targetPrice,
  };

  if (source.equipmentCosts !== undefined) input.equipmentCosts = source.equipmentCosts;
  if (source.equipmentTotalCost !== undefined) input.equipmentTotalCost = source.equipmentTotalCost;
  if (source.laborPlan !== undefined) input.laborPlan = source.laborPlan;
  if (source.quoteType !== undefined) input.quoteType = source.quoteType;
  if (source.hasSections !== undefined) input.hasSections = source.hasSections;
  if (source.wantedPrice !== undefined) input.wantedPrice = source.wantedPrice;
  // ponytail: selectedPrices may still reference source bidRequestIds — label-only, harmless
  if (source.selectedPrices !== undefined) input.selectedPrices = source.selectedPrices;
  if (source.manualSupplierBids !== undefined) input.manualSupplierBids = source.manualSupplierBids;
  if (source.targetPrices !== undefined) input.targetPrices = source.targetPrices;
  if (source.marginPercentByChapter !== undefined)
    input.marginPercentByChapter = source.marginPercentByChapter;
  if (source.pricingChapterOverrides !== undefined)
    input.pricingChapterOverrides = source.pricingChapterOverrides;
  if (source.chapterNames !== undefined) input.chapterNames = source.chapterNames;
  if (source.subChapterNames !== undefined) input.subChapterNames = source.subChapterNames;
  if (source.marginInputs !== undefined) input.marginInputs = source.marginInputs;
  if (source.boqSource !== undefined) input.boqSource = source.boqSource;

  return input;
}
